import { type Action, assetSchema, nftSchema } from '@0xtorch/core'
import { z } from 'zod'
import { bigDecimalTextSchema, createUnionSchema } from '../utils/schemas'

export const transactionCategories = [
  'blank',
  'borrow',
  'deposit',
  'fee',
  'income',
  'insufficient',
  'repayment',
  'replace',
  'trade',
  'transfer',
  'value-down',
  'value-up',
  'withdraw',
] as const
export type TransactionCategory = (typeof transactionCategories)[number]
const transactionCategorySchema = createUnionSchema(transactionCategories)

export const transactionSubCategories = [
  // borrow
  'bond',
  // fee
  'burn',
  'bridge-fee',
  'derivative-fee',
  'interest-fee',
  'liquidation-loss',
  'other-fee',
  'pay',
  'realize-loss',
  'stolen',
  'transaction-fee',
  'trade-loss',
  // income
  'airdrop',
  'cash-back',
  'derivative-profit',
  'game-reward',
  'gift',
  'hardfork',
  'interest',
  'liquidation-profit',
  'mining',
  'nft-royalty',
  'other-income',
  'realize-profit',
  'staking-reward',
  'trade-income',
  // replace
  'bridge',
  // trade
  'mint',
] as const
export type TransactionSubCategory = (typeof transactionSubCategories)[number]
const transactionSubCategorySchema = createUnionSchema(transactionSubCategories)

export const borrowSubCategories = ['bond'] as const
export type BorrowSubCategory = (typeof borrowSubCategories)[number]
export const isBorrowSubCategory = (
  subCategory: string,
): subCategory is BorrowSubCategory =>
  borrowSubCategories.includes(subCategory as BorrowSubCategory)

export const feeSubCategories = [
  'burn',
  'bridge-fee',
  'derivative-fee',
  'interest-fee',
  'liquidation-loss',
  'other-fee',
  'pay',
  'realize-loss',
  'stolen',
  'transaction-fee',
  'trade-loss',
] as const
export type FeeSubCategory = (typeof feeSubCategories)[number]
export const isFeeSubCategory = (
  subCategory: string,
): subCategory is FeeSubCategory =>
  feeSubCategories.includes(subCategory as FeeSubCategory)

export const incomeSubCategories = [
  'airdrop',
  'cash-back',
  'derivative-profit',
  'game-reward',
  'gift',
  'hardfork',
  'interest',
  'liquidation-profit',
  'mining',
  'nft-royalty',
  'other-income',
  'realize-profit',
  'staking-reward',
  'trade-income',
] as const
export type IncomeSubCategory = (typeof incomeSubCategories)[number]
export const isIncomeSubCategory = (
  subCategory: string,
): subCategory is IncomeSubCategory =>
  incomeSubCategories.includes(subCategory as IncomeSubCategory)

export const replaceSubCategories = ['bridge'] as const
export type ReplaceSubCategory = (typeof replaceSubCategories)[number]
export const isReplaceSubCategory = (
  subCategory: string,
): subCategory is ReplaceSubCategory =>
  replaceSubCategories.includes(subCategory as ReplaceSubCategory)

export const tradeSubCategories = ['mint'] as const
export type TradeSubCategory = (typeof tradeSubCategories)[number]
export const isTradeSubCategory = (
  subCategory: string,
): subCategory is TradeSubCategory =>
  tradeSubCategories.includes(subCategory as TradeSubCategory)

const transactionTransferSchema = z.object({
  asset: assetSchema,
  amount: bigDecimalTextSchema,
  price: bigDecimalTextSchema,
  zeroCompleted: z.boolean(),
  cost: bigDecimalTextSchema,
  balance: bigDecimalTextSchema,
  borrowing: bigDecimalTextSchema,
  feePl: bigDecimalTextSchema,
  incomePl: bigDecimalTextSchema,
  tradePl: bigDecimalTextSchema,
  separateTradePl: bigDecimalTextSchema.optional(),
  nftTradePl: bigDecimalTextSchema.optional(),
  nftCapitalPl: bigDecimalTextSchema.optional(),
  separateRealizedPl: bigDecimalTextSchema.optional(),
  realizedPl: bigDecimalTextSchema.optional(),
  airdropPl: bigDecimalTextSchema.optional(),
  totalPl: bigDecimalTextSchema,
})

export type TransactionTransfer = z.infer<typeof transactionTransferSchema>
export type NftAcquiredTimestamps = Map<string, number>

export const transactionSchema = z.object({
  actionId: z.string(),
  order: z.number(),
  timestamp: z.number().nonnegative(),
  category: transactionCategorySchema,
  subType: z.number().optional(),
  subCategory: transactionSubCategorySchema.optional(),
  crossId: z.string().optional(),
  comment: z.string().optional(),
  target: nftSchema.optional(),
  ins: z.array(transactionTransferSchema),
  outs: z.array(transactionTransferSchema),
})

export type Transaction = z.infer<typeof transactionSchema>

export type TransactionWithAction = Transaction & {
  action: Action
}
